/**
 * API Utility
 * Provides helper functions for making authenticated API requests to the backend
 */

/**
 * Get authentication headers for API requests
 * @returns {Object} Headers object with authentication if available
 */
export const getAuthHeaders = () => {
  // In a real implementation, you might include JWT or other auth tokens here
  // For now, we'll just return an empty object since our backend uses IP whitelisting
  return {
    // Add any required headers for your backend API
    'X-Requested-With': 'XMLHttpRequest'
  };
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - The API endpoint (without base URL)
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Object>} The parsed JSON response
 */
// No retry configuration - all calls will be made exactly once

export const apiRequest = async (endpoint, options = {}) => {
  // Get backend URL from environment variable or auto-detect from current URL
  let baseUrl = process.env.REACT_APP_API_BASE_URL || '';
  
  // If REACT_APP_API_BASE_URL is not set, auto-detect from current URL
  if (!baseUrl && typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    baseUrl = `http://${currentHost}:5000`;
    console.log(`[API] Auto-detected backend URL from browser: ${baseUrl} (hostname: ${currentHost})`);
  } else if (baseUrl) {
    console.log(`[API] Using backend URL from REACT_APP_API_BASE_URL: ${baseUrl}`);
  }
  
  if (!baseUrl) {
    throw new Error('REACT_APP_API_BASE_URL is not configured. Please set it in your .env file.');
  }
  
  // Ensure base URL doesn't end with a slash
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  // Ensure endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${cleanBaseUrl}${normalizedEndpoint}`;

  console.log('API Request:', {
    method: options.method || 'GET',
    url,
    endpoint
  });

  try {
    // IMPORTANT: Extract headers and body separately to preserve body
    const { headers: optionsHeaders, body: optionsBody, ...restOfOptions } = options;
    
    // Merge headers properly
    const mergedHeaders = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(optionsHeaders || {})
    };
    
    // Build final options with body explicitly included
    const defaultOptions = {
      method: options.method || 'GET',
      headers: mergedHeaders,
      credentials: 'include', // Include cookies if using session-based auth
      ...restOfOptions, // Other options like method override, etc.
      ...(optionsBody !== undefined ? { body: optionsBody } : {}) // Explicitly include body if present
    };

    // Log request details for debugging BEFORE fetch
    console.log('[API] === Request Debug Info ===');
    console.log('[API] Options received:', {
      hasBody: !!options.body,
      bodyType: typeof options.body,
      bodyLength: typeof options.body === 'string' ? options.body.length : 'N/A',
      optionsKeys: Object.keys(options)
    });
    console.log('[API] Final fetch options:', {
      method: defaultOptions.method,
      hasBody: !!defaultOptions.body,
      bodyType: typeof defaultOptions.body,
      bodyLength: defaultOptions.body ? (typeof defaultOptions.body === 'string' ? defaultOptions.body.length : 'not string') : 0,
      bodyValue: defaultOptions.body ? (typeof defaultOptions.body === 'string' ? defaultOptions.body.substring(0, 200) : String(defaultOptions.body).substring(0, 200)) : 'NONE',
      headersKeys: Object.keys(defaultOptions.headers || {})
    });

    if (defaultOptions.body) {
      try {
        const bodyPreview = typeof defaultOptions.body === 'string' ? JSON.parse(defaultOptions.body) : defaultOptions.body;
        console.log('[API] Body parsed successfully:', {
          hasCustomerId: !!bodyPreview?.customer_id,
          customerId: bodyPreview?.customer_id,
          hasLineItems: !!bodyPreview?.line_items,
          lineItemsCount: bodyPreview?.line_items?.length || 0,
          paymentMethod: bodyPreview?.payment_method,
          allKeys: Object.keys(bodyPreview || {})
        });
      } catch (e) {
        console.warn('[API] Could not parse body:', e.message);
        console.log('[API] Raw body (first 500 chars):', typeof defaultOptions.body === 'string' ? defaultOptions.body.substring(0, 500) : String(defaultOptions.body));
      }
    } else {
      console.error('[API] ⚠️⚠️⚠️ NO BODY IN FETCH OPTIONS! ⚠️⚠️⚠️');
    }
    console.log('[API] ==========================');

    const response = await fetch(url, defaultOptions);
    const responseData = await response.json().catch(() => ({}));

    // Handle error statuses
    if (!response.ok) {
      // Prefer server-provided error/message fields
      const serverMessage = (
        (typeof responseData === 'string' && responseData) ||
        responseData?.message ||
        responseData?.error ||
        responseData?.error?.message ||
        (Array.isArray(responseData?.errors) && responseData.errors[0]?.message) ||
        response.statusText ||
        'API request failed'
      );
      const error = new Error(serverMessage);
      error.status = response.status;
      error.data = responseData;
      error.url = url;
      error.method = defaultOptions.method || 'GET';
      throw error;
    }

    return responseData;
  } catch (error) {
    // Ensure we log enough context to diagnose
    console.error('API request failed:', {
      message: error?.message,
      status: error?.status,
      method: error?.method,
      url: error?.url || url,
      data: error?.data
    });
    throw error;
  }
};

/**
 * Make a GET request
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The response data
 */
export const get = (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  return apiRequest(url, { 
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
};

/**
 * Make a POST request
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<Object>} The response data
 */
// Helper function to safely stringify objects with circular references
const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  });
};

export const post = (endpoint, data = {}) => {
  // Clean the data object to remove any circular references
  const cleanData = JSON.parse(safeStringify(data));
  
  return apiRequest(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(cleanData)
  });
};

/**
 * Make a PUT request
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<Object>} The response data
 */
export const put = (endpoint, data = {}) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(data)
  });
};

/**
 * Make a DELETE request
 * @param {string} endpoint - The API endpoint
 * @returns {Promise<Object>} The response data
 */
export const del = (endpoint) => {
  return apiRequest(endpoint, { 
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
};

// Export all functions
export default {
  get,
  post,
  put,
  delete: del,
  request: apiRequest,
  getAuthHeaders
};
